import React, { useState } from 'react';
import {
    Grid, Typography, MenuItem, Select, InputLabel, FormControl,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    Checkbox, FormControlLabel, TextField, useTheme, Box
} from '@mui/material';
import baseStats from '../../data/classes/baseStats';

const yellowBuffs = { Strength: 62, Dexterity: 62, Quickness: 62 };
const supremacyBuffs = { Strength: 151, Dexterity: 151, Quickness: 93 };

const realms = ['Albion', 'Midgard', 'Hibernia'];
const classesByRealm = {
    Albion: ['Armsman', 'Cabalist', 'Cleric', 'Friar', 'Heretic', 'Infiltrator', 'Mauler', 'Mercenary', 'Minstrel', 'Necromancer', 'Paladin', 'Reaver', 'Scout', 'Sorcerer', 'Theurgist', 'Wizard'],
    Midgard: ['Berserker', 'Bonedancer', 'Healer', 'Hunter', 'Mauler', 'Runemaster', 'Savage', 'Shadowblade', 'Shaman', 'Skald', 'Spiritmaster', 'Thane', 'Valkyrie', 'Warlock', 'Warrior'],
    Hibernia: ['Animist', 'Bainshee', 'Bard', 'Blademaster', 'Champion', 'Druid', 'Eldritch', 'Enchanter', 'Hero', 'Mauler', 'Mentalist', 'Nightshade', 'Ranger', 'Valewalker', 'Vampiir', 'Warden'],
};
const races = Object.keys(baseStats);
const trackedStats = ['Strength', 'Dexterity', 'Quickness', 'Constitution', 'Intelligence', 'Piety', 'Empathy', 'Charisma'];
const mopLevels = {
    0: 0,
    1: 2,
    2: 4,
    3: 6,
    4: 9,
    5: 12,
    6: 15,
    7: 18,
    8: 21,
    9: 25,
};


const Calculators = () => {
    const theme = useTheme();
    const [realm, setRealm] = useState('Albion');
    const [charClass, setCharClass] = useState(classesByRealm['Albion'][0]);
    const [race, setRace] = useState('Briton');

    const [bonusStats, setBonusStats] = useState({});
    const [templateStats, setTemplateStats] = useState({});
    const [useYellowBuffs, setUseYellowBuffs] = useState(false);
    const [useSupremacy, setUseSupremacy] = useState(false);

    const [parrySkill, setParrySkill] = useState(0);
    const [blockSkill, setBlockSkill] = useState(0);
    const [evadeSkill, setEvadeSkill] = useState(0);
    const [mop, setMop] = useState(0);

    const getStat = (stat) => {
        const base = baseStats[race]?.[stat] || 0;
        const bonus = bonusStats[stat] || 0;
        const buff = useSupremacy ? supremacyBuffs[stat] || 0 : useYellowBuffs ? yellowBuffs[stat] || 0 : 0;
        const template = templateStats[stat] || 0;
        return { base, bonus, buff, template, total: base + bonus + buff + template };
    };

    const calcParry = () => {
        const dex = getStat('Dexterity').total;
        const mopBonus = mopLevels[mop] || 0;
        return (5 + (((dex * 2 - 100) / 40) + (parrySkill / 2) + mopBonus)).toFixed(2);
    };

    const calcBlock = () => {
        const dex = getStat('Dexterity').total;
        return (5 + (((dex * 2 - 100) / 40) + (blockSkill / 2))).toFixed(2);
    };

    const calcEvade = () => {
        const qui = getStat('Quickness').total;
        return (5 + (((qui * 2 - 100) / 40) + (evadeSkill / 2))).toFixed(2);
    };

    return (
        <Box p={4}>
            <Typography variant="h4" color="primary" gutterBottom>
                DAoC Stat Calculators
            </Typography>

            <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                        <InputLabel>Realm</InputLabel>
                        <Select value={realm} onChange={e => {
                            const r = e.target.value;
                            setRealm(r);
                            setCharClass(classesByRealm[r][0]);
                        }}>
                            {realms.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                        <InputLabel>Class</InputLabel>
                        <Select value={charClass} onChange={e => setCharClass(e.target.value)}>
                            {classesByRealm[realm].map(cls => <MenuItem key={cls} value={cls}>{cls}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                        <InputLabel>Race</InputLabel>
                        <Select value={race} onChange={e => setRace(e.target.value)}>
                            {races.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>

            <Box my={2}>
                <FormControlLabel
                    control={<Checkbox checked={useYellowBuffs} onChange={e => {
                        setUseYellowBuffs(e.target.checked);
                        if (e.target.checked) setUseSupremacy(false);
                    }} />}
                    label="Yellow Realm Enhancer Buffs"
                />
                <FormControlLabel
                    control={<Checkbox checked={useSupremacy} onChange={e => {
                        setUseSupremacy(e.target.checked);
                        if (e.target.checked) setUseYellowBuffs(false);
                    }} />}
                    label="Supremacy Pot Buffs"
                />
            </Box>

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ backgroundColor: theme.palette.primary.light }}>
                            <TableCell><strong>Stat</strong></TableCell>
                            <TableCell>Base</TableCell>
                            <TableCell>Bonus</TableCell>
                            <TableCell>Buff</TableCell>
                            <TableCell>Template</TableCell>
                            <TableCell><strong>Total</strong></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {trackedStats.map(stat => {
                            const { base, bonus, buff, template, total } = getStat(stat);
                            return (
                                <TableRow key={stat}>
                                    <TableCell>{stat}</TableCell>
                                    <TableCell>{base}</TableCell>
                                    <TableCell>
                                        <TextField
                                            type="number"
                                            size="small"
                                            value={bonus}
                                            onChange={(e) =>
                                                setBonusStats(prev => ({ ...prev, [stat]: parseInt(e.target.value || '0', 10) }))
                                            }
                                        />
                                    </TableCell>
                                    <TableCell>{buff}</TableCell>
                                    <TableCell>
                                        <TextField
                                            type="number"
                                            size="small"
                                            value={template}
                                            onChange={(e) =>
                                                setTemplateStats(prev => ({ ...prev, [stat]: parseInt(e.target.value || '0', 10) }))
                                            }
                                        />
                                    </TableCell>
                                    <TableCell><strong>{total}</strong></TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box mt={4}>
                <Typography variant="h6" color="primary">Combat Skill Inputs</Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                        <TextField fullWidth label="Parry Skill" type="number" value={parrySkill} onChange={e => setParrySkill(+e.target.value)} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField fullWidth label="Block Skill" type="number" value={blockSkill} onChange={e => setBlockSkill(+e.target.value)} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField fullWidth label="Evade Skill" type="number" value={evadeSkill} onChange={e => setEvadeSkill(+e.target.value)} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth>
                            <InputLabel>Mastery of Parrying</InputLabel>
                            <Select
                                value={mop}
                                onChange={(e) => setMop(+e.target.value)}
                                label="Mastery of Parrying"
                            >
                                {Object.keys(mopLevels).map((level) => (
                                    <MenuItem key={level} value={level}>
                                        Level {level} — {mopLevels[level]}%
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </Box>

            <Box mt={4}>
                <Typography variant="h6" color="primary">Calculated Chances</Typography>
                <Typography>🛡️ Parry Chance: <strong>{calcParry()}%</strong></Typography>
                <Typography>🛡️ Block Chance: <strong>{calcBlock()}%</strong></Typography>
                <Typography>👟 Evade Chance: <strong>{calcEvade()}%</strong></Typography>
            </Box>
        </Box>
    );
};

export default Calculators;
